// src/components/Agent/AgentView.jsx
import React, { useState, useEffect } from 'react';
import { useStore } from '../../store/useStore';
import { agentApi } from '../../services/api';
import { getSocket } from '../../hooks/useSocket';
import toast from 'react-hot-toast';
import { Bot, Zap, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function AgentView() {
  const { agentStatus, agentFindings, setAgentFindings, setAgentStatus } = useStore();
  const [query, setQuery] = useState('');
  const [investigations, setInvestigations] = useState([]);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    agentApi.investigations().then(setInvestigations).catch(() => {});
  }, []);

  async function triggerInvestigation() {
    if (!query.trim()) return;
    setRunning(true);
    setAgentStatus('thinking');
    try {
      const result = await agentApi.investigate({ query, triggerReason: query });
      if (result.success) {
        toast.success('Investigation complete');
        const updated = await agentApi.investigations();
        setInvestigations(updated);
      } else {
        toast.error(result.error || 'Investigation failed');
      }
    } finally {
      setRunning(false);
    }
  }

  const statusColors = { RUNNING: '#f59e0b', COMPLETED: '#22c55e', FAILED: '#ef4444' };
  const statusIcons  = { RUNNING: '⚡', COMPLETED: '✅', FAILED: '❌' };

  return (
    <div className="flex h-full">
      {/* Left: Investigation list */}
      <div className="w-72 shrink-0 flex flex-col"
        style={{ borderRight: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>🤖 AI Investigations</div>

          {/* Trigger form */}
          <textarea
            value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Ask the agent... e.g. 'Why are GCP errors spiking?'"
            className="w-full rounded px-3 py-2 text-xs resize-none outline-none"
            style={{
              background: 'var(--bg-tertiary)', border: '1px solid var(--border-2)',
              color: 'var(--text)', minHeight: 72,
            }}
          />
          <button onClick={triggerInvestigation} disabled={running || !query.trim()}
            className="w-full mt-2 py-2 rounded text-xs font-semibold flex items-center justify-center gap-2"
            style={{
              background: running ? 'rgba(139,92,246,.1)' : 'rgba(139,92,246,.2)',
              color: '#a78bfa', border: '1px solid rgba(139,92,246,.4)',
              cursor: running ? 'not-allowed' : 'pointer',
            }}>
            {running ? (
              <><div className="flex gap-0.5"><span className="thinking-dot"/><span className="thinking-dot"/><span className="thinking-dot"/></div>Investigating…</>
            ) : (
              <><Zap size={12} /> Run Investigation ↗</>
            )}
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
          {investigations.map(inv => (
            <div key={inv._id} onClick={() => setSelected(inv)}
              className="rounded p-2.5 cursor-pointer transition-all"
              style={{
                background: selected?._id === inv._id ? 'rgba(139,92,246,.15)' : 'var(--bg-tertiary)',
                border: `1px solid ${selected?._id === inv._id ? 'rgba(139,92,246,.4)' : 'var(--border)'}`,
              }}>
              <div className="flex items-center gap-1.5 mb-1">
                <span style={{ color: statusColors[inv.status] }}>{statusIcons[inv.status]}</span>
                <span className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>
                  {inv.query || inv.triggerReason || 'Scheduled scan'}
                </span>
              </div>
              <div className="flex gap-2 text-xs" style={{ color: 'var(--text-3)' }}>
                <span className="mono">{new Date(inv.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                {inv.durationMs && <span>{Math.round(inv.durationMs / 1000)}s</span>}
                {inv.confidence && <span className="ml-auto" style={{ color: '#a78bfa' }}>{inv.confidence}% conf</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Investigation detail */}
      <div className="flex-1 overflow-y-auto p-4">
        {selected ? (
          <InvestigationDetail investigation={selected} />
        ) : agentFindings ? (
          <InvestigationDetail investigation={{ findings: agentFindings.findings, ...agentFindings }} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-4"
            style={{ color: 'var(--text-3)' }}>
            <Bot size={48} style={{ opacity: 0.3 }} />
            <p className="text-sm">Select an investigation or run a new one</p>
          </div>
        )}
      </div>
    </div>
  );
}

function InvestigationDetail({ investigation }) {
  return (
    <div className="max-w-3xl">
      <div className="rounded-xl p-5 mb-4"
        style={{ background: 'rgba(139,92,246,.08)', border: '1px solid rgba(139,92,246,.3)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg"
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)' }}>🤖</div>
          <div>
            <div className="text-sm font-semibold">ObserveAI Agent Analysis</div>
            <div className="text-xs" style={{ color: '#a78bfa' }}>
              {investigation.confidence ? `${investigation.confidence}% confidence` : 'Completed'}
            </div>
          </div>
          {investigation.durationMs && (
            <div className="ml-auto text-xs mono flex items-center gap-1" style={{ color: 'var(--text-3)' }}>
              <Clock size={11} /> {Math.round(investigation.durationMs / 1000)}s
            </div>
          )}
        </div>

        {/* Root cause */}
        {investigation.rootCause && (
          <div className="mb-4 p-3 rounded-lg"
            style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)' }}>
            <div className="text-xs font-bold mb-1" style={{ color: '#f87171' }}>🎯 Root Cause</div>
            <div className="text-xs" style={{ color: 'var(--text-2)' }}>{investigation.rootCause}</div>
          </div>
        )}

        {/* Full findings */}
        <div className="text-xs leading-relaxed whitespace-pre-wrap"
          style={{ color: 'var(--text-2)' }}>
          {investigation.findings || 'No findings yet...'}
        </div>

        {/* Recommendations */}
        {investigation.recommendations?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-bold mb-2" style={{ color: '#4ade80' }}>✅ Recommendations</div>
            <ul className="flex flex-col gap-1.5">
              {investigation.recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
                  <span style={{ color: '#4ade80', flexShrink: 0 }}>→</span>
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions executed */}
        {investigation.actionsExecuted?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)' }}>Tools used</div>
            <div className="flex flex-wrap gap-1">
              {investigation.actionsExecuted.map((a, i) => (
                <span key={i} className="mono text-xs px-2 py-0.5 rounded"
                  style={{ background: 'rgba(139,92,246,.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,.2)' }}>
                  {a.split('(')[0]}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
